import type { QueryClient } from '@tanstack/react-query';

import type { ProductHit } from '@libs/algolia';

import { buildCartProductSkuKeyMap, fetchCartProductsBySkus, lineItemSkuToProductMapKey } from './cartProductsByIds';

const ORDER_CONFIRMATION_PRODUCT_LOOKUP_CALLER = 'web:cart:orderConfirmation-client';

type LineItemWithSku = { sku: string | number; webStyleId?: string; masterProductKey?: string | number };

type FulfillmentGroupWithItems = { items: LineItemWithSku[] };

/** Safe product id / SKU for Algolia lookup — only string and number; never `String(object)`. */
function trimNonEmptyProductId(value: string | number | null | undefined): string | undefined {
    if (value == null) {
        return undefined;
    }
    if (typeof value === 'string') {
        const t = value.trim();
        return t === '' ? undefined : t;
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
        return String(value);
    }
    return undefined;
}

function collectSkuIdsOnly(groups: FulfillmentGroupWithItems[]): Set<string> {
    const idSet = new Set<string>();
    for (const group of groups) {
        for (const item of group.items) {
            const sku = trimNonEmptyProductId(item.sku);
            if (sku != null) {
                idSet.add(sku);
            }
        }
    }
    return idSet;
}

function collectMasterProductKeysStillMissingWebStyle(groups: FulfillmentGroupWithItems[]): Set<string> {
    const idSet = new Set<string>();
    for (const group of groups) {
        for (const item of group.items) {
            if (item.webStyleId != null && String(item.webStyleId).trim() !== '') {
                continue;
            }
            const mpk = trimNonEmptyProductId(item.masterProductKey);
            if (mpk != null) {
                idSet.add(mpk);
            }
        }
    }
    return idSet;
}

function getHitFromMapKey(productMap: Map<number | undefined, ProductHit>, idString: string): ProductHit | undefined {
    const key = lineItemSkuToProductMapKey(idString);
    return key == null ? undefined : productMap.get(key);
}

function resolveProductHit(
    item: LineItemWithSku,
    productMap: Map<number | undefined, ProductHit>,
    products: ProductHit[]
): ProductHit | undefined {
    const sku = trimNonEmptyProductId(item.sku);
    if (sku != null) {
        const fromSku = getHitFromMapKey(productMap, sku);
        if (fromSku) {
            return fromSku;
        }
    }

    const mpk = trimNonEmptyProductId(item.masterProductKey);
    if (mpk == null) {
        return undefined;
    }

    const fromMpkKey = getHitFromMapKey(productMap, mpk);
    if (fromMpkKey) {
        return fromMpkKey;
    }

    return products.find(p => String(p.masterProductID ?? '') === mpk);
}

function assignWebStyleIdsFromHits(
    groups: FulfillmentGroupWithItems[],
    productMap: Map<number | undefined, ProductHit>,
    products: ProductHit[]
): void {
    for (const group of groups) {
        for (const item of group.items) {
            const product = resolveProductHit(item, productMap, products);
            const masterProductID = product?.masterProductID;
            if (masterProductID != null) {
                item.webStyleId = String(masterProductID);
            }
        }
    }
}

function hasAnyLineItems(groups: FulfillmentGroupWithItems[]): boolean {
    return groups.some(g => g.items.length > 0);
}

/**
 * Matches cart checkout enrichment in `useCartState` (Algolia `masterProductID` → `webStyleId`).
 * Order-by-id responses typically omit `webStyleId`; purchase analytics then must resolve it the same way as
 * `view_cart` / `add_shipping_info` / `add_payment_info`.
 *
 * **Two-phase lookup:** (1) fetch by line SKUs only so the React Query key often matches the cart’s
 * `fetchCartProductsBySkus` cache; (2) if any lines still lack `webStyleId`, fetch by `masterProductKey` values
 * (proxy accepts SKUs or web style ids) and merge hits before assigning again.
 */
export async function enrichLineItemsWithWebStyleIds<T extends { fulfillmentGroups: FulfillmentGroupWithItems[] }>(
    data: T,
    queryClient: QueryClient
): Promise<T> {
    const { fulfillmentGroups } = data;
    if (!hasAnyLineItems(fulfillmentGroups)) {
        return data;
    }

    const skuIds = collectSkuIdsOnly(fulfillmentGroups);
    const missingWebStyleMpkIds = collectMasterProductKeysStillMissingWebStyle(fulfillmentGroups);
    const needsMpkPassWithoutSkuFetch = skuIds.size === 0 && missingWebStyleMpkIds.size > 0;

    if (skuIds.size === 0 && !needsMpkPassWithoutSkuFetch) {
        return data;
    }

    const enriched = structuredClone(data);
    const groups = enriched.fulfillmentGroups;
    let allProducts: ProductHit[] = [];

    if (skuIds.size > 0) {
        const sortedSkus = [...skuIds].sort((a, b) => a.localeCompare(b));
        allProducts = await fetchCartProductsBySkus(queryClient, sortedSkus, ORDER_CONFIRMATION_PRODUCT_LOOKUP_CALLER);
        assignWebStyleIdsFromHits(groups, buildCartProductSkuKeyMap(allProducts), allProducts);
    }

    const mpkIds = collectMasterProductKeysStillMissingWebStyle(groups);
    if (mpkIds.size > 0) {
        const sortedMpk = [...mpkIds].sort((a, b) => a.localeCompare(b));
        const more = await fetchCartProductsBySkus(queryClient, sortedMpk, ORDER_CONFIRMATION_PRODUCT_LOOKUP_CALLER);
        allProducts = [...allProducts, ...more];
        assignWebStyleIdsFromHits(groups, buildCartProductSkuKeyMap(allProducts), allProducts);
    }

    return enriched;
}
